import React, { useContext, useEffect, useState } from 'react'
import RegisterLayout from '../profileSetupV2/crewProfileSetup/RegisterLayout'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import InputFieldV2 from '../../lib/input/InputFieldV2'
import PhoneInputV2 from '../../lib/input/Phoneinputv2'
import { Link, useNavigate } from 'react-router-dom'
import { routes } from '../../utils/routesConstants'
import { cookieServiceParam, errorMessage, placeholder } from '../../utils/constants'
import { apiConstants } from '../../utils/apiConstants'
import { EMAIL_VALIDATION, MOBILE_VALIDATION, PASS_VALIDATION, REQUIRED_VALIDATION } from '../../lib/input/validation-rules'
import { crewSignUpFormData } from '../../data/formData'
import { ErrorMessage } from '@hookform/error-message'
import { makeGetCall, makePostCall } from '../../effects/requests'
import { endPoints } from '../../utils/endpoints'
import { ApiResponseType, ROLE_TYPE } from '../../types'
import { emptyFunction, showToast, validatePhoneNumber } from '../../utils'
import { MyContext } from '../../context'
import { cookieService } from '../../services'
import { setToken } from '../../global/FCM/FCM'

const CrewSignUp = () => {

    const navigate = useNavigate();

    const userType = cookieService.getCookie(cookieServiceParam.userType) || ROLE_TYPE.TYPE_CREW

    const { updateUserData } = useContext(MyContext)

    const [isSecure, setIsSecure] = useState<any>(true);

    // useForm hook and set default behavior/values
    const { ...methods } = useForm({
        mode: 'all',
        defaultValues: { ...crewSignUpFormData() },
        reValidateMode: 'onBlur',
    });

    const onSubmit: SubmitHandler<any> = (values: any) => {
        const payload: any = {
            email: values?.[apiConstants.email],
            password: values?.[apiConstants.password],
            first_name: values?.[apiConstants?.first_name] || '',
            last_name: values?.[apiConstants?.last_name] || '',
            mobile_number: `${values?.dial_code?.dial_code}${values?.[apiConstants.mobile_number]}`,
            user_type: userType,
            is_pp_accepted: true,
            is_tnc_accepted: true,
            is_password_changed: true,
        }
        if (!validatePhoneNumber(payload?.[apiConstants.mobile_number])) {
            showToast(errorMessage.invalid_mobile_msg, 'error', 'mobile')
            return
        }
        checkEmailExists(payload);
    };


    const checkEmailExists = (apiPayload: any) => {
        makePostCall(endPoints.CHECKEMAIL, { email: apiPayload?.[apiConstants.email] }).then((res: ApiResponseType<any>) => {
            if (res.status.code == 200) {
                if (res?.data?.available) {
                    doSignup(apiPayload);
                } else {
                    showToast("Email already registered. Sign In", "error");
                    navigate('/sign-in');
                }
            }
        })
    }

    const doSignup = (payload: any) => {
        makePostCall(endPoints.SIGNUP, payload).then((res: ApiResponseType<any>) => {
            if (res.status?.code == 200) {
                updateUserData(res);
                saveDeviceToken();
                sendVerificationMail(payload?.[apiConstants.email]);
            }
        }).catch(emptyFunction)
    }

    const sendVerificationMail = (email: any) => {
        makeGetCall(endPoints.VERIFYEMAIL, { email: email }).then((res: ApiResponseType<any>) => {
            if (res.status?.code == 200) {
                cookieService.saveCookie(cookieServiceParam.userType, 'C')
                navigate('/verify-email');
            }
        }).catch(emptyFunction)
    }

    const saveDeviceToken = async () => {
        const params: any = { device_type: 'WEB' }
        if (cookieService.getCookie(cookieServiceParam.browserToken)) {
            params.token = cookieService.getCookie(cookieServiceParam.browserToken)
        } else {
            const fcmToken = await setToken(emptyFunction);
            params.token = fcmToken
        }
        if (!params?.token) return
        makePostCall(endPoints.DEVICE_TOKEN, params).then(emptyFunction).catch(emptyFunction)
    }

    useEffect(() => {
        methods.register('terms_accepted', {
            required: 'Please accept Terms and Conditions'
        });
    }, [methods]);

    const termsAccepted = methods.watch(apiConstants.terms_accepted) || false;

    return (
        <RegisterLayout>
            <FormProvider {...methods}>
                <form
                    onSubmit={methods.handleSubmit(onSubmit)}
                    noValidate
                >
                    <div className="register-step">
                        {/* Heading block */}
                        <div className="register-step__heading-block">
                            <h1 className="register-step__title">
                                Your Details
                            </h1>
                            <p className="register-step__description">
                                We need you to help us with some basic information for your account
                                creation. Here are our{" "}
                                <Link to={`${routes.legals}/tnc`}>Terms and Conditions</Link>. Please read them
                                carefully. We are GDRP compliant, and only share the data you want
                                us to.
                            </p>
                        </div>

                        <hr className="register-step__divider" />

                        <div className="personal-info__form">
                            <div className="personal-info__row">
                                <div className="personal-info__field">
                                    <label className="personal-info__label">First name</label>
                                    <InputFieldV2
                                        name={apiConstants.first_name}
                                        type="text"
                                        placeholder="Enter your first name"
                                        rules={REQUIRED_VALIDATION('First name')}
                                    />
                                </div>
                                <div className="personal-info__field">
                                    <label className="personal-info__label">Last name</label>
                                    <InputFieldV2
                                        name={apiConstants.last_name}
                                        type="text"
                                        placeholder="Enter your last name"
                                        rules={REQUIRED_VALIDATION('Last name')}
                                    />
                                </div>
                            </div>

                            <div className="personal-info__row">
                                <div className="personal-info__field">
                                    <label className="personal-info__label">Email</label>
                                    <InputFieldV2
                                        placeholder={placeholder.email}
                                        name={apiConstants.email}
                                        type='email'
                                        rules={EMAIL_VALIDATION}
                                    />
                                </div>
                                <PhoneInputV2
                                    name={apiConstants.mobile_number}
                                    countryName={apiConstants.dial_code}
                                    label="Phone number"
                                    placeholder={placeholder.contactNumber}
                                    rules={MOBILE_VALIDATION}
                                />
                            </div>

                            <div className="personal-info__form">
                                <div className="personal-info__field">
                                    <label className="personal-info__label">Password</label>
                                    <InputFieldV2
                                        placeholder={placeholder.password}
                                        name={apiConstants.password}
                                        type={isSecure ? 'password' : 'text'}
                                        rules={PASS_VALIDATION('')}
                                        rightIconPress={() => setIsSecure((prvState: any) => !prvState)}
                                        rightIcon={isSecure ? '/assets/images/eye-on.svg' : '/assets/images/eye-off.svg'}
                                    />
                                </div>
                            </div>

                        </div>

                        <hr className="register-step__divider personal-info__mid-divider" />

                        <div className="register-step__footer-left">
                            <label className="personal-info__terms">
                                <input
                                    type="checkbox"
                                    checked={termsAccepted as never}
                                    onChange={(e) => {
                                        methods.setValue('terms_accepted', e.target.checked, {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                        });
                                    }}
                                    className="personal-info__checkbox"
                                />
                                <span>
                                    I agree with{" "}
                                    <Link to={`${routes.legals}/tnc`}>Terms and Conditions</Link>{" "}
                                    and{" "}
                                    <Link to={`${routes.legals}/pp`}>Privacy Policy</Link>
                                </span>
                            </label>

                            <button
                                className="btn-next btn-next--yellow"
                                type='submit'
                            // disabled={!methods?.formState?.isValid}
                            >
                                Next
                            </button>
                        </div>
                        <ErrorMessage
                            errors={methods.formState.errors}
                            name={apiConstants.terms_accepted}
                            render={({ message }) => (
                                <span className="input-field-error-text">
                                    {message}
                                </span>
                            )}
                        />
                    </div>
                </form>
            </FormProvider>
        </RegisterLayout>
    )
}

export default CrewSignUp